import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import {
  denyRelatedPerson,
  getRelatedPersonIdentifierValue,
  getRelatedPersonResourceId,
  verifyRelatedPerson,
} from "@/services/fhir/relatedperson/relatedPersonVerification";

interface UseRelatedPersonVerificationOptions {
  patientId: string;
  onAfterVerify?: (entry: any) => void | Promise<void>;
  onAfterDeny?: (entry: any, resourceId: string) => void | Promise<void>;
  showSuccessToast?: boolean;
}

export function useRelatedPersonVerification({
  patientId,
  onAfterVerify,
  onAfterDeny,
  showSuccessToast = true,
}: UseRelatedPersonVerificationOptions) {
  const [pendingAction, setPendingAction] = useState<{
    resourceId: string;
    type: "verify" | "deny";
  } | null>(null);

  const isRowLoading = useCallback(
    (resourceId: string) => pendingAction?.resourceId === resourceId,
    [pendingAction]
  );

  const isVerifyLoading = useCallback(
    (resourceId: string) =>
      pendingAction?.resourceId === resourceId &&
      pendingAction.type === "verify",
    [pendingAction]
  );

  const isDenyLoading = useCallback(
    (resourceId: string) =>
      pendingAction?.resourceId === resourceId &&
      pendingAction.type === "deny",
    [pendingAction]
  );

  const handleVerify = useCallback(
    async (entry: any) => {
      const resourceId = getRelatedPersonResourceId(entry);
      const subjectId = getRelatedPersonIdentifierValue(entry);

      if (!resourceId || !subjectId || !patientId || isRowLoading(resourceId)) {
        return;
      }

      setPendingAction({ resourceId, type: "verify" });
      try {
        await verifyRelatedPerson({
          relatedPersonId: resourceId,
          patientId,
          subjectId,
        });

        if (showSuccessToast) {
          toast.success("Verificação realiza com sucesso  !");
        }

        await onAfterVerify?.(entry);
      } catch {
        toast.warn(
          "Não foi possível realizar a verificação, tente novamente!"
        );
      } finally {
        setPendingAction(null);
      }
    },
    [patientId, isRowLoading, onAfterVerify, showSuccessToast]
  );

  const handleDeny = useCallback(
    async (entry: any) => {
      const resourceId = getRelatedPersonResourceId(entry);

      if (!resourceId || isRowLoading(resourceId)) {
        return;
      }

      setPendingAction({ resourceId, type: "deny" });
      try {
        await denyRelatedPerson(resourceId);
        await onAfterDeny?.(entry, resourceId);
      } catch {
        toast.warn(
          "Não foi possível realizar a operação, tente novamente!"
        );
      } finally {
        setPendingAction(null);
      }
    },
    [isRowLoading, onAfterDeny]
  );

  return {
    isRowLoading,
    isVerifyLoading,
    isDenyLoading,
    handleVerify,
    handleDeny,
    isVerificationInProgress: pendingAction !== null,
  };
}
